/**
 * @file generate-routes.tsx
 * @description API-driven динамический генератор маршрутов для React Router. 
 * Поддерживает вложенные маршруты, автоматическую авторизацию и Layout-based архитектуру.
 * Интегрируется с бэкэнд API для получения структуры меню и автоматической генерации путей.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react, react-router-dom]
 * @related-files [./private-route.tsx, ./index.ts]
 */

import { Route, Routes as ReactRoutes } from 'react-router-dom';
import { PrivateRoute } from './private-route';
import { IBasePage } from '@src/types/page';

/**
 * Конфигурация маршрута для генератора
 */
interface IRouteConfig {
    layout: React.ComponentType;
    component: any;
    path?: string;
    isPublic?: boolean;
    routes: IBasePage[];
}


/**
 * Функция для преобразования вложенной структуры маршрутов в плоский массив
 * @param routes - массив конфигураций маршрутов
 * @returns плоский массив маршрутов для обработки React Router
 */
const generateFlattenRoutes = (routes: IBasePage[]): IBasePage[] => {
    return routes.reduce<IBasePage[]>(
        (acc, { ...route }) => [
            ...acc,
            route
        ],
        []
    );
};

/**
 * Основная функция генерации маршрутов на основе конфигурации
 * @param mainRoutes - массив конфигураций основных маршрутов
 * @returns React компонент с настроенными маршрутами
 */
export const renderRoutes = (mainRoutes: IRouteConfig[]) => {
    const Routes = () => {
        const layouts = mainRoutes.map(
            ({ layout: Layout, routes, component: Component, path: Path, isPublic = false }: IRouteConfig, index: number) => {
                // Если нет подмаршрутов, создаем простой маршрут
                if (!(routes?.length > 0)) {
                    return <Route key={`${index}_${Path}`} element={<Component />} path={Path} />;
                }

                // Создаем маршрут с Layout и вложенными подмаршрутами
                return (
                    <Route key={index} element={<Layout />} path={Path || '/'}>
                        <Route element={<PrivateRoute isPublic={isPublic} />}>
                            {routes?.length > 0 &&
                                generateFlattenRoutes(routes).map(({ path, component: Comp, ...compProps }: IBasePage, key: number) => {
                                    if (!path) {
                                        return null;
                                    }

                                    return (
                                        <Route
                                            key={key}
                                            element={Comp ? <Comp {...compProps} /> : <Component {...compProps} />}
                                            path={path}
                                        />
                                    );
                                })}
                        </Route>
                    </Route>
                );
            }
        );

        return <ReactRoutes>{layouts}</ReactRoutes>;
    };

    return Routes;
};
